// subscriptionsService.js
// Per-shop SaaS subscription state. Billing itself is hosted by Stripe — this
// wraps the four server endpoints that read state and mint redirect URLs.
//
// The billing customer here is on the Jobvious PLATFORM account, and is NOT the
// Connect payouts account already on `installer_location.stripe_account_id`.
// Two different Stripe customers exist per shop; do not conflate them.

import { HTTPService } from './httpService';

const API = new HTTPService();

/**
 * One shop's subscription state, plus the plans it may be assigned.
 *
 * Returns the whole envelope — `{ subscription, availablePlans }` — because the
 * read already carries the plan catalogue and a second GET /subscription-plans
 * would be a redundant round trip. `availablePlans` holds TWO rows per tier
 * (monthly and annual), already ordered by sort_order.
 */
export const getForLocation = async (locationId) => {
    const { data } = await API.get(`/installer-locations/${locationId}/subscription`);
    return data.data || data;
};

/**
 * Assign a plan and a start date to a shop. Jobvious admin only — the server
 * guards with assertIsJobviousAdmin and 409s once a live subscription exists,
 * so this is only reachable before the shop subscribes.
 *
 * `start_date` is 'YYYY-MM-DD' and becomes the billing anchor: checkout sets
 * trial_end from it, and bills immediately when it has already passed.
 */
export const assignPlan = async (locationId, { tier, start_date }) => {
    const { data } = await API.put(
        `/installer-locations/${locationId}/subscription/plan`,
        { tier, start_date }
    );
    return data.data || data;
};

/**
 * Hosted Checkout URL for a new subscription. Body is `{ interval, success_url,
 * cancel_url }` — the tier comes from the location's `assigned_tier`, server-side.
 * A client-sent tier is read by nothing.
 * 409 if the shop already has a non-terminal subscription — send them to the Portal.
 * 409 if no plan has been assigned yet.
 */
export const startCheckout = async (locationId, body) => {
    const { data } = await API.post(`/installer-locations/${locationId}/subscription/checkout`, body);
    return data.data || data;
};

/**
 * Hosted Billing Portal URL — payment method + invoice history.
 * 409 if the shop has no billing customer yet (i.e. never subscribed).
 */
export const openPortal = async (locationId, body) => {
    const { data } = await API.post(`/installer-locations/${locationId}/subscription/portal`, body);
    return data.data || data;
};

/**
 * The server's own words for a failure.
 *
 * Reads BOTH `error` and `message`: the task endpoints proved these envelopes
 * are inconsistent across routes, and an empty message is the failure mode we
 * spent a day removing. Never returns empty.
 */
export const subscriptionErrorMessage = (err, fallback = 'Something went wrong. Please try again.') => {
    const data = (err && err.response && err.response.data) || {};
    const raw = data.error || data.message;
    const text = typeof raw === 'string' ? raw.trim() : '';
    return text || fallback;
};

const SubscriptionsAPI = { getForLocation, assignPlan, startCheckout, openPortal, subscriptionErrorMessage };
export default SubscriptionsAPI;
